import React, { createContext, useContext, useEffect, useState } from 'react';
import { UserProfile } from '@/types';

interface AuthContextProps {
    isAuthenticated: boolean;
    accessToken: string | null;
    idToken?: string | null;
    user: UserProfile | null;
    login: () => void;
    logout: () => void;
    getAuthToken: () => string | null;
    isLoading: boolean;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

interface AuthProviderProps {
    children: React.ReactNode;
    authUrl: string; // e.g. "https://auth-dev.orionex.id"
    clientId: string; // The specific slug/client ID of this frontend app
    redirectUri: string; // e.g. "https://my-app.com/callback"
    mode: 'first-party' | 'third-party';
}

// Security Utils
async function generateCodeVerifier() {
    const array = new Uint32Array(56 / 2);
    window.crypto.getRandomValues(array);
    return Array.from(array, dec => ('0' + dec.toString(16)).substr(-2)).join('');
}

async function generateCodeChallenge(verifier: string) {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await window.crypto.subtle.digest('SHA-256', data);
    return btoa(String.fromCharCode(...new Uint8Array(digest)))
        .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children, authUrl, clientId, redirectUri, mode }) => {
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
    const [accessToken, setAccessToken] = useState<string | null>(null);
    const [idToken, setIdToken] = useState<string | null>(null);
    const [user, setUser] = useState<any>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        // Initial setup and callback handling
        const initializeAuth = async () => {
            // Bypass auth for local development
            if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
                const dummyUser: UserProfile = {
                    id: 'local-dev-user-id',
                    email: 'admin@localhost',
                    status: 'active',
                    full_name: 'Local Developer',
                    is_superuser: true,
                    app_permissions: {
                        "rolling_skeleton": {
                            "admin": true,
                            "read": true,
                            "write": true
                        }
                    }
                };
                setUser(dummyUser);
                setAccessToken('dummy-local-token');
                setIsAuthenticated(true);
                setIsLoading(false);
                return;
            }

            // 1. Check if we're returning from an OAuth callback
            const urlParams = new URLSearchParams(window.location.search);
            const code = urlParams.get('code');

            if (code && mode === 'third-party') {
                // CSRF Protection: Validate state
                const state = urlParams.get('state');
                const storedState = sessionStorage.getItem('oidc_state');
                if (!state || state !== storedState) {
                    console.error("CSRF Validation Failed: State mismatch");
                    setIsLoading(false);
                    return;
                }

                // Exchange code for tokens (PKCE + OIDC)
                const verifier = sessionStorage.getItem('pkce_verifier');
                if (verifier) {
                    try {
                        const params = new URLSearchParams();
                        params.append('grant_type', 'authorization_code');
                        params.append('client_id', clientId);
                        params.append('redirect_uri', redirectUri);
                        params.append('code', code);
                        params.append('code_verifier', verifier);

                        const response = await fetch(`${authUrl}/oauth/token`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                            body: params
                        });
                        const data = await response.json();
                        if (data.access_token) {
                            if (data.id_token) {
                                try {
                                    // Parse ID Token
                                    const payloadBase64 = data.id_token.split('.')[1];
                                    const base64 = payloadBase64.replace(/-/g, '+').replace(/_/g, '/');
                                    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
                                        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
                                    }).join(''));

                                    const decoded = JSON.parse(jsonPayload);
                                    const storedNonce = sessionStorage.getItem('oidc_nonce');

                                    if (decoded.nonce !== storedNonce) {
                                        throw new Error("Nonce mismatch (Replay Attack Prevention)");
                                    }

                                    const audArray = Array.isArray(decoded.aud) ? decoded.aud : [decoded.aud];
                                    if (!audArray.includes(clientId)) {
                                        throw new Error("Audience mismatch");
                                    }
                                    setIdToken(data.id_token);
                                } catch (e) {
                                    console.error("ID Token Validation Failed:", e);
                                    setIsLoading(false);
                                    return;
                                }
                            }

                            setAccessToken(data.access_token);
                            setUser(data.user);
                            setIsAuthenticated(true);
                            // Clean URL
                            window.history.replaceState({}, document.title, window.location.pathname);
                        }
                    } catch (err) {
                        console.error("Exchange Failed", err);
                    }
                }
                sessionStorage.removeItem('pkce_verifier');
                sessionStorage.removeItem('oidc_state');
                sessionStorage.removeItem('oidc_nonce');
            } else if (mode === 'first-party') {
                // In first-party mode, we might just `/userinfo` to see if our HTTP-only cookie is alive.
                try {
                    // 1. Obtain explicit access token using the HTTP-Only session cookie
                    const refreshRes = await fetch(`${authUrl}/api/v1/auth/refresh`, {
                        method: 'POST',
                        credentials: 'include',
                        headers: {
                            'X-Requested-With': 'XMLHttpRequest'
                        }
                    });

                    if (!refreshRes.ok) throw new Error("No active session");

                    const tokenData = await refreshRes.json();
                    const fetchedToken = tokenData.access_token;

                    if (fetchedToken) {
                        setAccessToken(fetchedToken);
                    }

                    // 2. Fetch /api/v1/users/me which explicitly requires a Bearer token even in first-party
                    const headers: HeadersInit = {};
                    if (fetchedToken) {
                        headers['Authorization'] = `Bearer ${fetchedToken}`;
                    }

                    const res = await fetch(`${authUrl}/api/v1/users/me`, {
                        credentials: 'include',
                        headers
                    });
                    if (res.ok) {
                        setIsAuthenticated(true);
                        setUser(await res.json());
                    }
                } catch (e) {
                    // Silent fail, user is not logged in.
                }
            }

            setIsLoading(false);
        };

        initializeAuth();
    }, [authUrl, mode]);

    const login = async () => {
        if (mode === 'third-party') {
            // Generate PKCE Context
            const verifier = await generateCodeVerifier();
            const challenge = await generateCodeChallenge(verifier);
            const state = await generateCodeVerifier();
            const nonce = await generateCodeVerifier();

            sessionStorage.setItem('pkce_verifier', verifier);
            sessionStorage.setItem('oidc_state', state);
            sessionStorage.setItem('oidc_nonce', nonce);

            // Redirect to Auth Provider
            const authEndpoint = new URL(`${authUrl}/authorize`);
            authEndpoint.searchParams.append('client_id', clientId);
            authEndpoint.searchParams.append('redirect_uri', redirectUri);
            authEndpoint.searchParams.append('response_type', 'code');
            authEndpoint.searchParams.append('scope', 'openid profile email');
            authEndpoint.searchParams.append('state', state);
            authEndpoint.searchParams.append('nonce', nonce);
            authEndpoint.searchParams.append('code_challenge', challenge);
            authEndpoint.searchParams.append('code_challenge_method', 'S256');

            window.location.href = authEndpoint.toString();
        } else {
            // First-Party Mode: Directly navigate to the IDP UI because 
            // it shares our cookie domain (e.g. `*.orionex.id`)
            window.location.href = `${authUrl}/?next=${encodeURIComponent(window.location.href)}`;
        }
    };

    const logout = () => {
        setAccessToken(null);
        setIdToken(null);
        setUser(null);
        setIsAuthenticated(false);
        const logoutUrl = new URL(`${authUrl}/oidc/logout`);
        logoutUrl.searchParams.append('post_logout_redirect_uri', window.location.origin);
        if (idToken) {
            logoutUrl.searchParams.append('id_token_hint', idToken);
        }
        window.location.href = logoutUrl.toString();
    };

    const getAuthToken = () => accessToken;

    return (
        <AuthContext.Provider value={{ isAuthenticated, accessToken, idToken, user, login, logout, getAuthToken, isLoading }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
